<template>
  <!--
    TASK-SF-10 -- the DESIGN-10 role-upgrade card. It is ONLY a new entry
    point into the EXISTING volunteer role-onboarding flow: on confirm it
    dispatches the existing `user/onboardRole` action -> POST
    /api/user/roles/volunteer, then `user/reconcileAfterRoleOnboarding` and
    hands the user to the existing volunteer path. It implements NO role
    logic of its own, adds NO dual-role semantics, and there is NO backend
    change (the D-04 consent record is TASK-SF-04A's).

    A CONFIRM step, never one-click, because `attachRole` throws unless
    `terms === true`.

    SX-02 (doc 22 §5.1): becoming a teacher is NEW acquisition -- while the
    central capability is OFF the CTA raises the shared Coming Soon dialog
    and the confirm step is unreachable. SX-01 (DESIGN-19): visual parity
    with the rendered V2 `.role-opportunity-card`; the behaviour above is
    untouched.
  -->
  <section class="volunteer-upgrade" data-testid="volunteer-upgrade-card">
    <span class="volunteer-upgrade__icon" aria-hidden="true">
      <student-home-icon name="teacher" :size="25" />
    </span>

    <div class="volunteer-upgrade__body-wrap">
      <template v-if="!confirming">
        <p class="volunteer-upgrade__eyebrow">
          {{ $t('profile.student.upgrade.title') }}
          <!-- SX-02: badged while becoming a teacher is Coming Soon -->
          <span
            v-if="!teacherExperienceAvailable"
            class="volunteer-upgrade__badge"
            data-testid="volunteer-upgrade-coming-soon"
            >{{ $t('studentComingSoon.badge') }}</span
          >
        </p>
        <h2 class="volunteer-upgrade__title">
          {{ $t('profile.student.upgrade.subtitle') }}
        </h2>
        <p class="volunteer-upgrade__body">
          {{ $t('profile.student.upgrade.body') }}
        </p>
        <button
          type="button"
          class="volunteer-upgrade__cta"
          data-testid="volunteer-upgrade-cta"
          @click="onCtaClick"
        >
          {{ $t('profile.student.upgrade.cta') }}
          <student-home-icon name="arrow" :size="16" />
        </button>
      </template>

      <template v-else>
        <h2 class="volunteer-upgrade__title">
          {{ $t('profile.student.upgrade.confirmHeading') }}
        </h2>
        <p class="volunteer-upgrade__body">
          {{ $t('profile.student.upgrade.confirmBody') }}
        </p>

        <label class="volunteer-upgrade__terms">
          <input
            type="checkbox"
            data-testid="volunteer-upgrade-terms"
            v-model="termsAccepted"
          />
          <span>{{ $t('profile.student.upgrade.termsLabel') }}</span>
        </label>

        <p
          v-if="error"
          class="volunteer-upgrade__error"
          role="alert"
          data-testid="volunteer-upgrade-error"
        >
          {{ $t('profile.student.upgrade.error') }}
        </p>

        <div class="volunteer-upgrade__actions">
          <button
            type="button"
            class="volunteer-upgrade__cta"
            data-testid="volunteer-upgrade-confirm"
            :disabled="!termsAccepted || submitting"
            @click="onConfirm"
          >
            <span
              v-if="submitting"
              class="volunteer-upgrade__spinner"
              aria-hidden="true"
            ></span>
            {{ $t('profile.student.upgrade.confirmButton') }}
          </button>
          <button
            type="button"
            class="volunteer-upgrade__cancel"
            data-testid="volunteer-upgrade-cancel"
            :disabled="submitting"
            @click="reset"
          >
            {{ $t('profile.student.upgrade.cancel') }}
          </button>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import i18n from '@/i18n'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

const ATTACHED_OUTCOMES = [
  'ROLE_ATTACHED',
  'ROLE_COMPLETED',
  'ROLE_ALREADY_HELD',
]

export default {
  name: 'VolunteerUpgradeCard',
  components: { StudentHomeIcon },
  data() {
    return {
      confirming: false,
      termsAccepted: false,
      submitting: false,
      error: false,
    }
  },
  computed: {
    // SX-02 (doc 22 §5.1): becoming a teacher is NEW acquisition. While the
    // central capability is OFF the CTA raises the shared Coming Soon dialog
    // and the confirm step -- and therefore `user/onboardRole` -- is
    // unreachable. With it ON the accepted TASK-SF-10 flow runs unchanged.
    teacherExperienceAvailable() {
      return (
        this.$store.getters[
          'featureFlags/isStudentTeacherExperienceAvailable'
        ] === true
      )
    },
  },
  methods: {
    onCtaClick() {
      if (!this.teacherExperienceAvailable) {
        this.$store.dispatch('app/comingSoon/show', {
          variant: 'teacher',
          reason: 'register',
        })
        return
      }
      this.confirming = true
    },
    reset() {
      this.confirming = false
      this.termsAccepted = false
      this.error = false
    },
    async onConfirm() {
      // `attachRole` throws unless terms === true; the button is already
      // gated on it, this is the belt-and-braces guard.
      if (!this.termsAccepted || this.submitting) return
      this.submitting = true
      this.error = false
      try {
        const payload = await this.$store.dispatch('user/onboardRole', {
          role: 'volunteer',
          data: { terms: true },
        })

        const attached = payload && ATTACHED_OUTCOMES.includes(payload.outcome)
        // ROLE_ATTACHED etc. is the success family; some deployments resolve
        // straight to an activeRole with no outcome field.
        if (attached || (payload && payload.activeRole === 'volunteer')) {
          // Reconcile with the server BEFORE navigating -- the dashboard
          // guard reads activeRole the instant this push lands. Same
          // sequence LoginView.submitRoleOnboarding uses.
          await this.$store.dispatch(
            'user/reconcileAfterRoleOnboarding',
            'volunteer'
          )
          this.$router.push(`/${i18n.locale}/dashboard`)
          return
        }
        this.error = true
      } catch (e) {
        this.error = true
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// SX-01: measured from the rendered V2 `.role-opportunity-card--teacher`
// (1440 / 390): a two-column card on a white -> teal-50 gradient with a
// 54px white icon tile; the phone tier tightens it to 46px / 15px.
$vu-teal-shadow: rgba(8, 125, 158, 0.09);

.volunteer-upgrade {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  align-items: start;
  gap: 14px;
  margin: 14px 0 20px;
  padding: 18px;
  border: 1px solid $borna-teal-100;
  border-radius: 21px;
  background: linear-gradient(135deg, $borna-surface, $borna-teal-50);
}

.volunteer-upgrade__icon {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: $borna-surface;
  box-shadow: 0 7px 20px $vu-teal-shadow;
  color: $borna-teal-700;
}

.volunteer-upgrade__body-wrap {
  min-width: 0;
}

// V2 `.role-opportunity-card .eyebrow`: 12px / 800, 18px line.
.volunteer-upgrade__eyebrow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
  color: $borna-indigo-600;
}

// SX-02: the same «به‌زودی» pill the Home cards use (HomeActionCard.vue),
// kept within the eyebrow's 18px line.
.volunteer-upgrade__badge {
  display: inline-block;
  padding: 1px 9px;
  border-radius: 999px;
  background: #fff7ed;
  color: #9a5b13;
  font-size: 11px;
  font-weight: $borna-fw-bold;
  line-height: 1.45;
  white-space: nowrap;
}

.volunteer-upgrade__title {
  margin: 5px 0 3px;
  font-size: 15px;
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  color: $borna-ink;
}

.volunteer-upgrade__body {
  max-width: 680px;
  margin: 0;
  font-size: 10px;
  line-height: 1.85;
  color: $borna-muted;
}

.volunteer-upgrade__cta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 11px;
  padding: 9px 12px;
  border: 0;
  border-radius: 11px;
  background: $borna-teal-700;
  color: #fff;
  font: inherit;
  font-size: 10px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    background: darken($borna-teal-700, 6%);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

// The arrow glyph points toward the reading direction in RTL.
html.is-rtl .volunteer-upgrade__cta svg {
  transform: scaleX(-1);
}

.volunteer-upgrade__terms {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0 0;
  font-size: 11px;
  line-height: 1.8;
  color: $borna-text;
}

.volunteer-upgrade__error {
  margin: 10px 0 0;
  padding: 8px 10px;
  border: 1px solid $borna-error-border;
  border-radius: $borna-radius-sm;
  background: $borna-error-bg;
  color: $borna-error-text;
  font-size: 11px;
  line-height: 1.8;
}

.volunteer-upgrade__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.volunteer-upgrade__cancel {
  margin-top: 11px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $borna-muted;
  font: inherit;
  font-size: 11px;
  font-weight: $borna-fw-bold;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: $borna-indigo-600;
  }
}

.volunteer-upgrade__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: volunteer-upgrade-spin 0.7s linear infinite;
}

@keyframes volunteer-upgrade-spin {
  to {
    transform: rotate(360deg);
  }
}

// V2 `@media (width<=620px)`
@include borna-phone {
  .volunteer-upgrade {
    grid-template-columns: 46px minmax(0, 1fr);
    padding: 15px;
  }

  .volunteer-upgrade__icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .volunteer-upgrade__cta {
    width: 100%;
    justify-content: center;
  }
}
</style>
